'use client';

import React, { ReactNode, useEffect, useRef, useState } from 'react';
import { useTranslations } from 'next-intl';
import clsx from 'clsx';
import { buildRoute } from '@headless-workspace/config';
import { CountrySelectorValue, HeaderSlotsValue } from '@headless-workspace/core-domain';
import { FIXED_HEADER_ID } from '@headless-workspace/core-ui';
import { GlowMenuButton, Icons, PropsWithClassName, PropsWithLocale } from '@headless-workspace/glow-ds';
import { Component } from '@headless-workspace/typings';
import {
    CategoryNavigation,
    CategoryValue,
    DataLayerClassName,
    getConnectedUserInfo,
    useDeviceType,
    useFixedHeader,
    useUserInfo,
    VipBrandsValue,
} from '../../../../../src';
import { HeaderBurgerMenuLogo } from './HeaderBurgerMenuLogo';
import { HeaderUtilities } from './HeaderUtilities';
import { MobileSearchMenu, SearchBar, SearchButton, SearchMenu, SearchProvider, useSearchStateContext } from './search';

export const SEARCH_MENU_ID = 'search-menu';

// 1 means that the element has to be fully visible to trigger the observer
const INTERSECTION_OBSERVER_THRESHOLD = 1;

export type HeaderProps = PropsWithClassName &
    PropsWithLocale & {
        topBannerChildren?: ReactNode | ReactNode[];
        isH1Logo: boolean;
        categories: CategoryValue[];
        vipBrands: VipBrandsValue;
        totalForFreeShipping: number;
        headerSlots?: HeaderSlotsValue;
        enablePriceExceptionLegalMentions: boolean;
        isEnableOffers?: boolean;
        countrySelector?: CountrySelectorValue;
        hostUrl: string | null;
    };

export const Header: Component<HeaderProps> = ({
    className,
    topBannerChildren,
    isH1Logo,
    categories,
    vipBrands,
    totalForFreeShipping,
    headerSlots,
    enablePriceExceptionLegalMentions,
    isEnableOffers,
    countrySelector,
    hostUrl,
    locale,
}) => {
    'use memo';

    // Global refs / hooks
    const t = useTranslations('Header');
    const mobileSearchBarRef = useRef<HTMLDivElement>(null);
    const deviceType = useDeviceType();
    const isDesktop = deviceType === 'desktop';

    // Search related context / hooks
    const { isSearchOpen } = useSearchStateContext();
    const [isMobileSearchBarVisible, setIsMobileSearchBarVisible] = useState(true);

    // Header's translation related refs / hooks
    const fixedHeaderRef = useRef<HTMLDivElement>(null);
    const persistentBannerRef = useRef<HTMLDivElement>(null);
    const { translate, isHeaderFullyVisible } = useFixedHeader(fixedHeaderRef, persistentBannerRef, isSearchOpen);

    // Mobile's burger menu button related hooks / logic
    const { result: userInfoResult } = useUserInfo();
    const userInfo = getConnectedUserInfo(userInfoResult);
    const label = userInfo
        ? t('connection.action.hello', { name: userInfo.firstName })
        : t('connection.action.loginOrRegister');
    const href = userInfo ? buildRoute(locale, 'account') : buildRoute(locale, 'connection');

    // Show search icon only when scroll bar is not visible on mobile
    useEffect(() => {
        const observer = new IntersectionObserver(
            ([entry]) => {
                setIsMobileSearchBarVisible(entry.isIntersecting);
            },
            {
                threshold: INTERSECTION_OBSERVER_THRESHOLD,
            },
        );

        if (mobileSearchBarRef.current) {
            observer.observe(mobileSearchBarRef.current);
        }

        return () => {
            observer.disconnect();
        };
    }, [mobileSearchBarRef]);

    return (
        <header className={clsx('contents flex-col bg-background-l0 mx-auto w-full', className)}>
            <SearchProvider locale={locale}>
                <MobileSearchMenu locale={locale} vipBrands={vipBrands} />
                <div ref={persistentBannerRef}>{topBannerChildren}</div>
                <div
                    id={FIXED_HEADER_ID}
                    ref={fixedHeaderRef}
                    className={clsx(
                        'sticky top-0 z-headerFixed',
                        isHeaderFullyVisible ? 'desktop:relative' : 'desktop:fixed desktop:left-0 desktop:right-0',
                    )}
                    style={isDesktop ? { transform: translate } : {}}
                >
                    <div
                        className={clsx(
                            'flex flex-col justify-center items-center',
                            'h-headerFixedMobile tablet:h-headerFixedTablet desktop:h-headerDesktop',
                            'bg-background-l0',
                            'tablet:border-b-medium tablet:border-solid tablet:border-b-border-primary',
                        )}
                    >
                        <div
                            className={clsx(
                                'flex-1 w-full flex justify-between items-center',
                                'px-1 desktop:px-1.5 gap-1 desktop:gap-1.5',
                                'desktop:w-maxWidth',
                            )}
                        >
                            <HeaderBurgerMenuLogo
                                locale={locale}
                                isH1Logo={isH1Logo}
                                burgerMenuAriaLabel={t('navigation.ariaLabel.burger')}
                            />
                            <div
                                className={clsx(
                                    'flex-1 flex justify-end tablet:justify-between tablet:gap-1 desktop:gap-1.5',
                                )}
                            >
                                <div
                                    className={clsx(
                                        (isMobileSearchBarVisible || isSearchOpen) && 'hidden',
                                        'p-0.5 tablet:hidden',
                                    )}
                                >
                                    <SearchButton />
                                </div>
                                <div className={'flex-1 hidden tablet:block'}>
                                    <SearchBar />
                                    <SearchMenu locale={locale} vipBrands={vipBrands} />
                                </div>
                                <HeaderUtilities
                                    locale={locale}
                                    hostUrl={hostUrl}
                                    isEnableOffers={isEnableOffers}
                                    totalForFreeShipping={totalForFreeShipping}
                                    vipBrands={vipBrands}
                                    enablePriceExceptionLegalMentions={enablePriceExceptionLegalMentions}
                                    headerSlots={headerSlots}
                                    countrySelector={countrySelector}
                                />
                            </div>
                        </div>
                        <CategoryNavigation
                            className={'hidden desktop:flex flex-1 justify-center overflow-hidden'}
                            locale={locale}
                            categories={categories}
                        />
                    </div>
                </div>
                {
                    /* Placeholder element to avoid CLS on desktop when header is detached */
                    isDesktop && <div className={clsx(!isHeaderFullyVisible ? 'block' : 'hidden', 'h-headerDesktop')} />
                }

                <div className={'bg-background-l0 relative tablet:hidden'}>
                    <div
                        ref={mobileSearchBarRef}
                        className={clsx('h-headerFixedTablet py-0.5 px-1 relative', isSearchOpen && 'z-searchBanner')}
                    >
                        <SearchBar />
                    </div>
                </div>
                <GlowMenuButton
                    Icon={Icons.Profile}
                    label={label}
                    ariaLabel={label}
                    href={href}
                    className={clsx(
                        'desktop:hidden h-headerScrollableTablet',
                        'border-solid border-b-medium border-border-primary',
                        'text-text-primary',
                        DataLayerClassName.connection,
                    )}
                    fontSize={'bodySmall'}
                    showArrow
                />
            </SearchProvider>
        </header>
    );
};
